// Add a Task, as a one-line bottom sheet: type the title (the phone keyboard's microphone can dictate it), pick Today or Tomorrow (Today is the default), Save. Nothing else is required.
// Shared by the + button and by the Today section on Home. onSaved runs after the task is saved so the caller can refresh.
import React, { useEffect, useState } from 'react';
import { useAppContext } from '../lib/AppContext';
import { supabase } from '../lib/supabase';

const iso = (d) => d.toISOString().slice(0, 10);

export default function AddTaskSheet({ open, onClose, onSaved, contact = null, company = null, assignedTo = '' }) {
  const { activePartner, showToast } = useAppContext();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('today');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    const name = contact && [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    setTitle(name ? `Follow up with ${name}${company?.name ? ` at ${company.name}` : ''}` : '');
    setDue('today');
  }, [open, contact, company]);
  if (!open) return null;

  const close = () => { setTitle(''); setDue('today'); onClose(); };
  async function save() {
    const t = title.trim();
    if (!t || saving) return;
    setSaving(true);
    const day = new Date();
    if (due === 'tomorrow') day.setDate(day.getDate() + 1);
    const { error } = await supabase.from('tasks').insert({ title: t, due_date: iso(day), horizon: due === 'today' ? 'Today' : 'This Week', priority: 'Normal', status: 'Open', task_type: 'todo', partner_id: activePartner ? activePartner.id : null, ...(assignedTo ? { assigned_to: assignedTo } : {}), ...(contact?.id ? { related_contact_id: contact.id } : {}), ...(company?.id ? { related_company_id: company.id } : {}) });
    setSaving(false);
    if (error) { if (showToast) showToast(error.message || 'Could not save the task.', 'error'); return; }
    if (showToast) showToast('Task added.');
    setTitle(''); setDue('today');
    if (onSaved) onSaved();
    onClose();
  }

  return (
    <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 800, display: 'flex', alignItems: 'flex-end' }}>
      <div role="dialog" aria-modal="true" aria-label="Add a Task" onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: '100%', borderRadius: '16px 16px 0 0', padding: '14px 14px calc(18px + env(safe-area-inset-bottom, 0px))', boxSizing: 'border-box' }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--gov-ink, #1a2133)', marginBottom: 10 }}>Add a Task</div>
        {contact && <div style={{ fontSize: 13, marginBottom: 8 }}>{[contact.first_name, contact.last_name].filter(Boolean).join(' ')}{company?.name ? ` · ${company.name}` : ''}</div>}
        <input autoFocus aria-label="Task" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} placeholder="What needs doing?"
          style={{ width: '100%', boxSizing: 'border-box', minHeight: 48, fontSize: 16, padding: '0 14px', border: '1px solid var(--gov-border-soft, #cbd5e1)', borderRadius: 10, marginBottom: 10 }} />
        <div role="radiogroup" aria-label="Due" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[['today', 'Today'], ['tomorrow', 'Tomorrow']].map(([k, label]) => (
            <button key={k} type="button" role="radio" aria-checked={due === k} onClick={() => setDue(k)} style={{ flex: 1, minHeight: 44, borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', background: due === k ? 'var(--gov-surface-2, #f1f5f9)' : '#fff', color: 'var(--gov-ink, #1a2133)', border: `1px solid ${due === k ? 'var(--gov-ink, #1a2133)' : 'var(--gov-border-soft, #cbd5e1)'}` }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={close} style={{ flex: 1, minHeight: 48, borderRadius: 10, fontSize: 16, fontWeight: 700, background: '#fff', border: '1px solid var(--gov-border-soft, #cbd5e1)', color: 'var(--gov-ink, #1a2133)', cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={save} disabled={!title.trim() || saving} style={{ flex: 2, minHeight: 48, borderRadius: 10, fontSize: 16, fontWeight: 700, background: 'var(--gov-ink, #1a2133)', border: 'none', color: '#fff', cursor: 'pointer', opacity: !title.trim() ? 0.5 : 1 }}>Save Task</button>
        </div>
      </div>
    </div>
  );
}
